import { usePostHog } from "@posthog/react";
import {
	Component,
	createElement,
	lazy,
	useEffect,
	type ComponentType,
	type ReactNode,
} from "react";

import { useStrings } from "../i18n/index.tsx";
import { isPostHogConfigured } from "../posthog.ts";
import { partReports } from "../stale-build.ts";
import { COL } from "../theme.ts";
import { Btn, Sheet } from "./primitives.tsx";

export interface LazyPart<P extends object> {
	/** Renders the part, suspending only while its chunk is still on the way. */
	Part: ComponentType<P>;
	/** Starts (or joins) the chunk's download. A failure is tried again by the next render that needs the part. */
	preload: () => Promise<ComponentType<P>>;
}

interface Registered {
	preload: () => Promise<unknown>;
	/** Forgets a failed load, so the next render downloads again. */
	retry: () => void;
}

const parts: Registered[] = [];

/**
 * A component kept out of the startup bundle (the world art: see the bundle budget).
 * Once its chunk is in, it renders directly: no Suspense frame on a later open.
 */
export function lazyPart<P extends object>(load: () => Promise<ComponentType<P>>): LazyPart<P> {
	let loaded: ComponentType<P> | null = null;
	let pending: Promise<ComponentType<P>> | null = null;
	let failed = false;
	const preload = (): Promise<ComponentType<P>> =>
		(pending ??= load().then(
			(c) => (loaded = c),
			(e: unknown) => {
				failed = true;
				throw e;
			},
		));
	const fresh = () =>
		lazy(() => {
			// A load that failed before this render asked (the idle preload, offline at
			// startup) is tried again. Safe from loops: React calls this once per lazy.
			if (failed) {
				failed = false;
				pending = null;
			}
			return preload().then((c) => ({ default: c }));
		});
	let Lazy = fresh();
	function Part(props: P) {
		return createElement(loaded ?? Lazy, props);
	}
	parts.push({
		preload,
		// Not on the rejection itself: React renders the part again to reach the
		// boundary, and a fresh lazy there would loop. React.lazy keeps a rejection
		// for good, hence a new one, whose factory downloads again.
		retry: () => {
			if (failed) Lazy = fresh();
		},
	});
	return { Part, preload };
}

/**
 * Loads every lazy part declared so far, so a test renders them as if bundled in.
 * Settled, not all: a part that fails to load shows its fallback where it renders.
 */
export const preloadParts = (): Promise<unknown> =>
	Promise.allSettled(parts.map((p) => p.preload()));

/** Runs `fn` once the page is idle (a timeout where requestIdleCallback is missing: Safari). Returns a cancel. */
export function whenIdle(fn: () => void): () => void {
	if (typeof requestIdleCallback === "function") {
		const id = requestIdleCallback(fn, { timeout: 3000 });
		return () => cancelIdleCallback(id);
	}
	const id = setTimeout(fn, 1500);
	return () => clearTimeout(id);
}

/** Starts a lazy part's download on idle; a failure is left to the render that needs it. */
export const preloadWhenIdle = (...ps: { preload: () => Promise<unknown> }[]): (() => void) =>
	whenIdle(() => {
		for (const p of ps) p.preload().catch(() => {});
	});

interface BoundaryProps {
	/** Shown instead of the part when its chunk cannot load. */
	fallback: ReactNode;
	/** Told of the failure, to report it (see `usePartError`). */
	onError?: (error: unknown) => void;
	children: ReactNode;
}

/**
 * Reports a part that could not load, once per part per page (every slot of a part
 * fails together). Tagged with the part, whether the browser thought it was online
 * and `stale_build`, so a dropped connection can be told from a stale build.
 */
export function usePartError(part: string): (error: unknown) => void {
	const posthog = usePostHog();
	return (error) => {
		if (isPostHogConfigured)
			void partReports.report(part, error, (e, tags) => posthog?.captureException(e, { ...tags }));
	};
}

/**
 * Catches a lazy part that failed to load: a tab left open across a deploy asks
 * for hashed chunks the new build deleted. Without it the error would reach the
 * root boundary and replace the whole app.
 */
export class PartBoundary extends Component<BoundaryProps, { failed: boolean }> {
	override state = { failed: false };
	static getDerivedStateFromError(): { failed: boolean } {
		return { failed: true };
	}
	override componentDidCatch(error: unknown): void {
		this.props.onError?.(error);
	}
	// Closing what failed (a sheet) lets the next open try the network again: the
	// connection may be back, though a stale build's chunks will not be.
	override componentWillUnmount(): void {
		if (this.state.failed) for (const p of parts) p.retry();
	}
	override render(): ReactNode {
		return this.state.failed ? this.props.fallback : this.props.children;
	}
}

/** A fallback for a passing beat whose part could not load: moves on as if it had played. */
export function SkipPart({ onSkip }: { onSkip: () => void }) {
	useEffect(onSkip, []);
	return null;
}

/** What an overlay shows when its part could not load: a way out, and a reload that fetches the current build. */
export function PartFailed({ onClose }: { onClose: () => void }) {
	const t = useStrings();
	return (
		<Sheet onClose={onClose}>
			<p role="alert" className="mb-4 text-center text-sm" style={{ color: COL.mute }}>
				{t("part.failed")}
			</p>
			<Btn onClick={() => location.reload()}>{t("part.reload")}</Btn>
		</Sheet>
	);
}
